import { assertUnreachable } from "@arti/utilities";

export const FEATURE_FLAG_NAMES = [
  "actor_system",
  "compile_recording",
  "repository_chat",
  "skill_mesh",
  "teams_chat_sdk",
] as const;

export type FeatureFlagName = (typeof FEATURE_FLAG_NAMES)[number];

export interface FeatureFlagDefinition {
  name: FeatureFlagName;
  hardDefault: boolean;
  snapshotKey: FeatureFlagName;
  orgOverridable: boolean;
  label: string;
  description: string;
}

export const FEATURE_FLAG_REGISTRY: Record<
  FeatureFlagName,
  FeatureFlagDefinition
> = {
  actor_system: {
    description:
      "Enables the actor system dashboard workspace (Arena: Rooms, Fleet, Templates) for the organization.",
    hardDefault: false,
    label: "Actor system",
    name: "actor_system",
    orgOverridable: true,
    snapshotKey: "actor_system",
  },
  compile_recording: {
    description:
      "Enables compile-recording delivery flow capture and review artifacts.",
    hardDefault: true,
    label: "Compile recording",
    name: "compile_recording",
    orgOverridable: true,
    snapshotKey: "compile_recording",
  },
  repository_chat: {
    description:
      "Enables the repository chat dashboard workspace and related user API routes.",
    hardDefault: false,
    label: "Repository chat",
    name: "repository_chat",
    orgOverridable: true,
    snapshotKey: "repository_chat",
  },
  skill_mesh: {
    description:
      "Enables the Skill Mesh MCP endpoint (/public/skill-mesh/mcp): an organization-shared catalog of Claude Code skills that members can search, read, create, and update.",
    hardDefault: false,
    label: "Skill Mesh",
    name: "skill_mesh",
    orgOverridable: true,
    snapshotKey: "skill_mesh",
  },
  teams_chat_sdk: {
    description:
      "Enables Teams chat SDK binding, webhook ingest, dashboard controls, realtime events, and outbox enqueue.",
    hardDefault: false,
    label: "Teams chat SDK",
    name: "teams_chat_sdk",
    orgOverridable: true,
    snapshotKey: "teams_chat_sdk",
  },
};

export function isFeatureFlagName(value: string): value is FeatureFlagName {
  return FEATURE_FLAG_NAMES.some((name) => name === value);
}

export function getFeatureFlagDefinition(
  flagName: FeatureFlagName,
): FeatureFlagDefinition {
  switch (flagName) {
    case "actor_system": {
      return FEATURE_FLAG_REGISTRY.actor_system;
    }
    case "compile_recording": {
      return FEATURE_FLAG_REGISTRY.compile_recording;
    }
    case "repository_chat": {
      return FEATURE_FLAG_REGISTRY.repository_chat;
    }
    case "skill_mesh": {
      return FEATURE_FLAG_REGISTRY.skill_mesh;
    }
    case "teams_chat_sdk": {
      return FEATURE_FLAG_REGISTRY.teams_chat_sdk;
    }
    default: {
      throw assertUnreachable(flagName);
    }
  }
}
